import React, { useState } from 'react';
import { Brain, TrendingUp, AlertCircle, Loader2, DollarSign, PieChart } from 'lucide-react';
import { motion } from 'framer-motion';
import * as tf from '@tensorflow/tfjs';

interface FinancialMetrics {
  revenue: number;
  expenses: number;
  cashFlow: number;
  profitMargin: number;
}

interface Prediction {
  nextMonth: number;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
}

export function SmartFinancialAssistant() {
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState<Prediction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mockHistoricalData = [
    { month: 'Jan', revenue: 50000 },
    { month: 'Feb', revenue: 55000 },
    { month: 'Mar', revenue: 52000 },
    { month: 'Apr', revenue: 58000 },
    { month: 'May', revenue: 62000 },
  ];

  const generatePredictions = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use TensorFlow.js for predictions
      const model = tf.sequential();
      model.add(tf.layers.dense({ units: 1, inputShape: [1] }));
      model.compile({ loss: 'meanSquaredError', optimizer: 'sgd' });

      // Convert data for TensorFlow
      const x = tf.tensor2d(mockHistoricalData.map((_, i) => [i]));
      const y = tf.tensor2d(mockHistoricalData.map(d => [d.revenue]));

      // Train model
      await model.fit(x, y, { epochs: 100 });

      // Make prediction
      const nextMonthPrediction = model.predict(
        tf.tensor2d([[mockHistoricalData.length]])
      ) as tf.Tensor;
      const predictionValue = (await nextMonthPrediction.data())[0];

      // Calculate trend
      const lastThreeMonths = mockHistoricalData.slice(-3).map(d => d.revenue);
      const trend =
        lastThreeMonths[2] > lastThreeMonths[0]
          ? 'up'
          : lastThreeMonths[2] < lastThreeMonths[0]
            ? 'down'
            : 'stable';

      setPredictions({
        nextMonth: predictionValue,
        confidence: 0.85,
        trend,
      });

      // Cleanup
      model.dispose();
      x.dispose();
      y.dispose();
      nextMonthPrediction.dispose();
    } catch (err) {
      console.error('Error generating predictions:', err);
      setError('Failed to generate financial predictions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-dark-card rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Brain className="h-6 w-6 text-primary-400" />
          <h2 className="text-xl font-semibold text-dark-text">Financial Assistant</h2>
        </div>
        <button
          onClick={generatePredictions}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <TrendingUp className="h-4 w-4" />
              <span>Generate Predictions</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-dark-bg rounded-lg p-4">
          <h3 className="text-lg font-medium text-dark-text mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary-400" />
            Current Metrics
          </h3>
          <div className="space-y-4">
            {mockHistoricalData.map((data, index) => (
              <motion.div
                key={data.month}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex justify-between items-center"
              >
                <span className="text-dark-muted">{data.month}</span>
                <span className="text-dark-text font-medium">${data.revenue.toLocaleString()}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {predictions && (
          <div className="bg-dark-bg rounded-lg p-4">
            <h3 className="text-lg font-medium text-dark-text mb-4 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-primary-400" />
              Predictions
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-dark-muted">Next Month Revenue</p>
                <p className="text-2xl font-semibold text-dark-text">
                  ${predictions.nextMonth.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-dark-muted">Confidence</p>
                <p className="text-dark-text font-medium">
                  {(predictions.confidence * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-dark-muted">Trend</p>
                <div className="flex items-center gap-2">
                  {predictions.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-400" />}
                  <span className="text-dark-text capitalize">{predictions.trend}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center text-red-400">
            <AlertCircle className="h-5 w-5 mr-2" />
            <p>{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}
